import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Globe,
  Heart,
  Info,
  Loader2,
  MessageCircle,
  Search,
  Share2,
  X,
} from "lucide-react";

import FeedPostCardHeader from "../../components/Clubs/FeedPostCardHeader";
import ClubThreadSidebar from "../../components/Clubs/ClubThreadSidebar";
import ThreadComposer from "../../features/comments/components/ThreadComposer.js";
import CommentThreadList from "../../features/comments/components/CommentThreadList.js";
import ClubImageLightbox from "../../components/Clubs/ClubImageLightbox";
import ClubPostMediaGallery from "../../components/Clubs/ClubPostMediaGallery";

import {
  buildClubCommentSearchPreview,
  formatClubCommentRelativeMeta,
} from "../../features/comments/utils/clubFeedPostUtils.js";
import {
  CLUB_FEED_ACTION_SHADOW,
  clubFeedActionBase,
  clubFeedHeartClass,
  clubFeedNeutralButtonClass,
  clubPostCardActionRowClass,
  clubPostCardArticleClass,
  clubPostCardBodyClass,
  clubPostCardBodyTextClass,
  clubPostCardHeaderClass,
  clubPostCardTitleClass,
} from "../../components/Clubs/feedPostCardPresentation";
import {
  clubThreadCommentsStackClass,
  clubThreadCommentsWrapClass,
  clubThreadComposerWrapClass,
  clubThreadContinueThreadButtonClass,
  clubThreadSearchBtnClass,
  clubThreadSectionClass,
  clubThreadShowMoreButtonClass,
  clubThreadSortMenuClass,
  clubThreadSortMenuItemClass,
  clubThreadSortTriggerClass,
  clubThreadToolsRowClass,
} from "../../features/comments/presentation/threadPresentation.js";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/**
 * Canonical forum/club thread layout: toolbar, post article, comments, sidebar chrome.
 * Presentation only — all data loading and mutations stay in `ClubPostThreadPage`.
 */
export default function CanonicalClubPostThreadShell({
  navigate,
  forumSurface,
  post,
  club,
  slug,
  effectiveSlug,
  postIdParam,
  viewerId,
  artImg,
  postHeading,
  postBodyRest,
  hasMediaOnlyBody,
  previewImages,
  actionErr,
  clubLoadWarning,
  commentsSectionRef,
  postMenuRef,
  postMenuOpen,
  setPostMenuOpen,
  canDelete,
  deleteBusy,
  onDelete,
  likeBusy,
  onToggleLike,
  liked,
  likeCount,
  scrollToComments,
  commentCount,
  onShare,
  shareHint,
  newCommentsBannerVisible,
  searchMode,
  refreshNewCommentsFromPoll,
  dismissNewCommentsBanner,
  replyText,
  setReplyText,
  sending,
  submitReply,
  commentSort,
  setCommentSort,
  sortMenuOpen,
  setSortMenuOpen,
  sortMenuRef,
  commentSearchText,
  setCommentSearchText,
  setSearchMode,
  commentSearchRemote,
  setCommentSearchRemote,
  searchCommentsTriggerRef,
  shouldFocusComposer,
  loadingComments,
  comments,
  commentsHasMore,
  loadingMoreComments,
  loadMoreComments,
  activeReplyTargetId,
  setActiveReplyTargetId,
  inlineReplyText,
  setInlineReplyText,
  inlineSending,
  submitInlineReply,
  highlightedCommentId,
  pendingFocusCommentId,
  onToggleCommentLike,
  onCommentMutated,
  setActionErr,
  openCommentSearchHit,
  firstRootContinuationPath,
  subtreeTruncated,
  previewOpen,
  setPreviewOpen,
  previewIndex,
  setPreviewIndex,
}) {
  const {
    accent,
    canParticipate,
    canModerateComments,
    clubHomePath,
    clubPublicPath,
    threadBackTargetPath,
    backNavWideLabel,
    breadcrumbContextLabel,
    showCommunityChrome,
    likeTitleWhenBlocked,
    emptyCommentsSecondaryHint,
  } = forumSurface;

  const feedHeartTokens = clubFeedHeartClass(accent, liked);
  const feedNeutralAction = clubFeedNeutralButtonClass();

  return (
    <div className="min-h-[60vh] w-full min-w-0 max-w-full overflow-x-hidden text-white pb-16 box-border">
      <div className="relative w-full min-w-0 max-w-full">
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/0 to-black/25" />
          <div className="absolute inset-0 [background:radial-gradient(980px_560px_at_18%_0%,rgba(255,255,255,0.07),transparent_62%)]" />
          <div
            className={cn(
              "absolute inset-0 [background:radial-gradient(980px_560px_at_86%_10%,transparent,transparent_62%)]",
              accent === "sky"
                ? "[background:radial-gradient(980px_560px_at_86%_10%,rgba(56,189,248,0.08),transparent_62%)]"
                : "[background:radial-gradient(980px_560px_at_86%_10%,rgba(16,185,129,0.075),transparent_62%)]"
            )}
          />
        </div>

        <div className="mx-auto w-full min-w-0 max-w-[1200px] px-3 sm:px-4 md:px-5 pt-4 sm:pt-6 pb-1 box-border">
          <div className="rounded-2xl border border-white/12 bg-white/[0.06] backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.45)] p-3 sm:p-4 flex flex-wrap items-center justify-between gap-2 sm:gap-3 w-full min-w-0 max-w-full">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => navigate(threadBackTargetPath)}
                className="inline-flex items-center gap-2 rounded-xl border border-white/12 bg-white/10 px-3.5 py-2 text-sm text-gray-100 hover:bg-white/[0.14] transition-colors shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{backNavWideLabel}</span>
                <span className="sm:hidden">Back</span>
              </button>
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 min-w-0 pl-1 border-l border-white/10 ml-1">
                <span>AnimeHouse</span>
                <ChevronRight className="h-3 w-3 opacity-50 shrink-0" />
                <Link
                  to={clubPublicPath}
                  className="truncate text-gray-200 hover:text-white transition-colors font-medium"
                >
                  {breadcrumbContextLabel}
                </Link>
                <ChevronRight className="h-3 w-3 opacity-50 shrink-0" />
                <span className="text-gray-400 truncate">Thread</span>
              </div>
            </div>
          </div>

          {actionErr ? (
            <div className="mt-3 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-2 text-sm text-red-200">
              {actionErr}
            </div>
          ) : null}

          {clubLoadWarning ? (
            <div
              className="mt-4 flex gap-3 rounded-xl border border-amber-400/20 bg-gradient-to-r from-amber-500/[0.12] to-amber-500/[0.04] px-4 py-3.5 text-sm"
              role="status"
            >
              <Info className="h-5 w-5 text-amber-200/90 shrink-0 mt-0.5" strokeWidth={2} />
              <div className="min-w-0">
                <p className="font-semibold text-amber-50/95">Club details unavailable</p>
                <p className="mt-1 text-amber-100/80 leading-relaxed text-sm">{clubLoadWarning}</p>
              </div>
            </div>
          ) : null}

          <div className="mt-5 sm:mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px] gap-5 lg:gap-6 lg:items-start w-full min-w-0 max-w-full">
            <div className="min-w-0 space-y-5 sm:space-y-6 w-full max-w-full">
              <article aria-label="Original club post" className={clubPostCardArticleClass}>
                <div className={clubPostCardHeaderClass}>
                  <FeedPostCardHeader
                    post={post}
                    club={club}
                    clubSlug={String(effectiveSlug || slug || post?.club_slug || "").trim()}
                    variant="thread"
                    menuOpen={postMenuOpen}
                    setMenuOpen={setPostMenuOpen}
                    menuRef={postMenuRef}
                    onShare={(e) => {
                      e?.stopPropagation?.();
                      onShare?.();
                    }}
                    onDelete={(e) => {
                      e?.stopPropagation?.();
                      onDelete?.();
                    }}
                    canDelete={canDelete}
                    deleteBusy={deleteBusy}
                  />
                </div>

                <div className={clubPostCardBodyClass}>
                  {hasMediaOnlyBody ? (
                    <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                      Media post
                    </p>
                  ) : null}
                  <h1 id="club-thread-title" className={clubPostCardTitleClass}>
                    {postHeading}
                  </h1>
                  {postBodyRest ? (
                    <div className={cn("mt-4", clubPostCardBodyTextClass)}>{postBodyRest}</div>
                  ) : null}
                  {!String(post.content || "").trim() && (previewImages.length || artImg) ? (
                    <p className="mt-4 text-sm text-gray-500 leading-relaxed border-l-2 border-emerald-400/25 pl-4 max-w-prose">
                      Media-only post — see below.
                    </p>
                  ) : null}

                  {previewImages.length ? (
                    <ClubPostMediaGallery
                      className="mt-6"
                      images={previewImages}
                      variant="thread"
                      onOpenAt={(idx) => {
                        setPreviewIndex(idx);
                        setPreviewOpen(true);
                      }}
                    />
                  ) : null}

                  {post.artwork_id && artImg ? (
                    <Link
                      to={`/arthouse/art/${post.artwork_id}`}
                      className="mt-6 flex min-w-0 w-full max-w-full gap-3 sm:gap-4 rounded-2xl border border-white/12 bg-black/30 p-3 sm:p-4 hover:border-white/20 hover:bg-black/35 transition-colors items-center"
                    >
                      <div className="h-[72px] w-[72px] rounded-xl overflow-hidden border border-white/10 shrink-0 bg-white/5">
                        <img src={artImg} alt="" className="h-full w-full object-cover" />
                      </div>
                      <div className="min-w-0 flex flex-col justify-center">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                          Linked artwork
                        </div>
                        <div className="mt-1 text-sm font-semibold text-white truncate">
                          {post.artwork_title || "View artwork"}
                        </div>
                      </div>
                    </Link>
                  ) : null}
                </div>

                <div className={clubPostCardActionRowClass}>
                  {shareHint ? (
                    <p className="mb-2 text-[11px] sm:text-xs font-medium text-emerald-300/90">{shareHint}</p>
                  ) : null}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 w-full min-w-0">
                    <div
                      className="flex flex-nowrap items-center gap-2 overflow-x-auto [-webkit-overflow-scrolling:touch] w-full min-w-0 pb-0.5 sm:pb-0"
                      role="toolbar"
                      aria-label="Post actions"
                    >
                      <button
                        type="button"
                        onClick={onToggleLike}
                        disabled={likeBusy || !viewerId || !canParticipate}
                        aria-pressed={liked}
                        aria-label={
                          liked ? `Unlike, ${likeCount} likes` : `Like, currently ${likeCount} likes`
                        }
                        title={
                          !viewerId
                            ? "Log in to like"
                            : !canParticipate
                              ? likeTitleWhenBlocked
                              : liked
                                ? "Unlike"
                                : "Like"
                        }
                        className={cn(
                          clubFeedActionBase,
                          feedHeartTokens,
                          CLUB_FEED_ACTION_SHADOW,
                          !viewerId || !canParticipate ? "opacity-50 cursor-not-allowed" : ""
                        )}
                      >
                        <Heart
                          className={cn(
                            "h-3.5 w-3.5 shrink-0",
                            liked ? "fill-current text-emerald-300" : "text-white/70"
                          )}
                        />
                        <span className="tabular-nums">{likeCount}</span>
                      </button>
                      <button
                        type="button"
                        onClick={scrollToComments}
                        title="Jump to discussion"
                        aria-label={`Open comments, ${commentCount} comments`}
                        className={feedNeutralAction}
                      >
                        <MessageCircle className="h-3.5 w-3.5 shrink-0" />
                        <span className="tabular-nums">{commentCount}</span>
                      </button>
                      <button
                        type="button"
                        onClick={onShare}
                        title="Share or copy link"
                        aria-label="Share or copy post link"
                        className={feedNeutralAction}
                      >
                        <Share2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        Share
                      </button>
                    </div>
                    <p className="text-[11px] sm:text-xs text-gray-500 text-center sm:text-right leading-relaxed w-full min-w-0 sm:w-auto sm:max-w-[220px] sm:shrink-0">
                      Keep it respectful and on-topic.
                    </p>
                  </div>
                </div>
              </article>

              {newCommentsBannerVisible && !searchMode ? (
                <div
                  className={cn(
                    clubThreadSectionClass,
                    "mb-3 flex flex-wrap items-center gap-2 border-white/15 bg-white/[0.06] py-2.5"
                  )}
                  role="status"
                >
                  <span className="min-w-0 flex-1 text-sm text-gray-200">
                    New comments — refresh to load them.
                  </span>
                  <button
                    type="button"
                    className={cn(clubFeedNeutralButtonClass(), "shrink-0 text-xs px-3 py-1.5")}
                    onClick={() => refreshNewCommentsFromPoll()}
                  >
                    Refresh
                  </button>
                  <button
                    type="button"
                    className={cn(
                      clubFeedNeutralButtonClass(),
                      "shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-100"
                    )}
                    aria-label="Dismiss new comments notice"
                    onClick={() => dismissNewCommentsBanner()}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              ) : null}

              <section
                id="club-thread-comments"
                ref={commentsSectionRef}
                className={clubThreadSectionClass}
              >
                <div className={clubThreadComposerWrapClass}>
                  <ThreadComposer
                    viewerId={viewerId}
                    canParticipate={canParticipate}
                    replyText={replyText}
                    sending={sending}
                    onReplyTextChange={setReplyText}
                    onSubmit={submitReply}
                    collapsible
                    collapsedPlaceholder="Join the conversation"
                    isSearchMode={searchMode}
                    searchText={commentSearchText}
                    onSearchTextChange={setCommentSearchText}
                    onExitSearchMode={() => {
                      setSearchMode(false);
                      setCommentSearchText("");
                      setCommentSearchRemote({ status: "idle", matches: [], errorMessage: "" });
                    }}
                    searchFocusReturnRef={searchCommentsTriggerRef}
                    autoFocus={shouldFocusComposer}
                    className="px-0 py-0 border-0"
                  />
                </div>

                <div className={clubThreadToolsRowClass}>
                  <div className="relative" ref={sortMenuRef}>
                    <button
                      type="button"
                      className={clubThreadSortTriggerClass}
                      onClick={() => setSortMenuOpen((v) => !v)}
                    >
                      Sort by
                      <span className="font-semibold capitalize text-gray-100">{commentSort}</span>
                      <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                    </button>
                    {sortMenuOpen ? (
                      <div className={clubThreadSortMenuClass}>
                        {[
                          { id: "new", label: "New" },
                          { id: "old", label: "Old" },
                          { id: "top", label: "Top" },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            className={clubThreadSortMenuItemClass(commentSort === opt.id)}
                            onClick={() => {
                              setCommentSort(opt.id);
                              setSortMenuOpen(false);
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <button
                    ref={searchCommentsTriggerRef}
                    type="button"
                    className={clubThreadSearchBtnClass}
                    aria-expanded={searchMode}
                    onClick={() => {
                      setSearchMode(true);
                      setSortMenuOpen(false);
                    }}
                  >
                    <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Search Comments
                  </button>
                </div>

                {subtreeTruncated ? (
                  <div
                    className="mt-3 rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2.5 text-xs sm:text-sm text-gray-500 leading-relaxed"
                    role="status"
                  >
                    Some replies under this page of comments are omitted.{" "}
                    {firstRootContinuationPath ? (
                      <>
                        <Link
                          to={firstRootContinuationPath}
                          className="text-emerald-200/90 hover:text-emerald-100 underline-offset-2 hover:underline"
                        >
                          View more replies
                        </Link>{" "}
                        to view more.
                      </>
                    ) : (
                      "View more replies."
                    )}
                  </div>
                ) : null}

                <div className={[clubThreadCommentsWrapClass, "pt-5 sm:pt-6"].join(" ")}>
                  {loadingComments ? (
                    <div className="space-y-5" aria-busy="true">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 animate-pulse"
                        >
                          <div className="flex gap-4">
                            <div className="h-10 w-10 rounded-2xl bg-white/10 shrink-0" />
                            <div className="flex-1 space-y-2 pt-1">
                              <div className="h-3.5 w-32 bg-white/10 rounded" />
                              <div className="h-3 w-full bg-white/10 rounded" />
                              <div className="h-3 w-[88%] bg-white/10 rounded" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={clubThreadCommentsStackClass}>
                      {searchMode ? (
                        <div
                          data-club-full-comment-search
                          className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 sm:px-4 sm:py-3.5 mb-3 sm:mb-3.5"
                          role="region"
                          aria-label="Full discussion comment search results"
                        >
                          <p className="text-[11px] sm:text-xs font-medium text-gray-400 mb-2">
                            Search all comments
                            <span className="text-gray-500 font-normal"> (all comments on this post)</span>
                          </p>
                          {!String(commentSearchText || "").trim() ? (
                            <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                              Type a keyword to search every comment on this post, including replies shown when you
                              tap &quot;View more replies.&quot;
                            </p>
                          ) : commentSearchRemote.status === "loading" ? (
                            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
                              <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                              Searching…
                            </div>
                          ) : commentSearchRemote.status === "error" ? (
                            <p className="text-xs sm:text-sm text-amber-200/90">
                              {commentSearchRemote.errorMessage || "Search failed."}
                            </p>
                          ) : commentSearchRemote.matches.length === 0 ? (
                            <p className="text-xs sm:text-sm text-gray-500" role="status">
                              No comments match that search in the full discussion.
                            </p>
                          ) : (
                            <>
                              <ul className="space-y-2 max-h-[min(360px,50vh)] overflow-y-auto pr-0.5">
                                {commentSearchRemote.matches.map((hit) => {
                                  const preview = buildClubCommentSearchPreview(hit, commentSearchText, 168);
                                  const excerpt = preview.excerpt || "No visible comment text.";
                                  const uname = hit?.username || "Member";
                                  const hitTime = formatClubCommentRelativeMeta(hit.created_at);
                                  const pid = hit?.parent_comment_id;
                                  const ctx =
                                    pid != null && Number.isFinite(Number(pid)) ? "Nested reply" : "Top-level";
                                  return (
                                    <li
                                      key={String(hit.id)}
                                      className="rounded-lg border border-white/[0.06] bg-black/20 px-3 py-2.5"
                                    >
                                      <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                            <span className="text-xs sm:text-sm font-semibold text-gray-100">
                                              {uname}
                                            </span>
                                            <time
                                              dateTime={hitTime.dateTimeAttr || undefined}
                                              className="text-[10px] sm:text-[11px] text-gray-500 tabular-nums"
                                              title={hitTime.title || undefined}
                                            >
                                              {hitTime.label || hitTime.title || "—"}
                                            </time>
                                            <span className="text-[10px] sm:text-[11px] text-gray-500">
                                              · {ctx}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-xs sm:text-sm text-gray-300/90 leading-relaxed break-words">
                                            {preview.highlightedExcerpt ? (
                                              <>
                                                {preview.highlightedExcerpt.before}
                                                <mark className="rounded bg-emerald-500/25 px-0.5 text-emerald-100">
                                                  {preview.highlightedExcerpt.match}
                                                </mark>
                                                {preview.highlightedExcerpt.after}
                                              </>
                                            ) : (
                                              excerpt
                                            )}
                                          </p>
                                          {preview.matchedIn === "username" ? (
                                            <p className="mt-1 text-[10px] sm:text-[11px] text-gray-500">
                                              Matched in username. Preview shows this comment.
                                            </p>
                                          ) : null}
                                          {preview.matchedIn === "raw" ? (
                                            <p className="mt-1 text-[10px] sm:text-[11px] text-gray-500">
                                              Matched in attached content. Preview shows this comment.
                                            </p>
                                          ) : null}
                                        </div>
                                        <button
                                          type="button"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={() => openCommentSearchHit(hit)}
                                          className={cn(
                                            clubThreadContinueThreadButtonClass,
                                            "shrink-0 gap-1"
                                          )}
                                          aria-label={`Open replies for comment ${hit.id} by ${uname}`}
                                        >
                                          Open
                                          <ChevronRight className="h-3.5 w-3.5 opacity-90" aria-hidden />
                                        </button>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                              {commentSearchRemote.searchTruncated ? (
                                <p
                                  className="mt-2 pt-2 border-t border-white/[0.06] text-[10px] sm:text-[11px] text-gray-500 leading-relaxed"
                                  role="status"
                                >
                                  Showing the first {commentSearchRemote.matches.length} matches. More may
                                  exist—try a more specific keyword.
                                </p>
                              ) : null}
                            </>
                          )}
                        </div>
                      ) : null}
                      <CommentThreadList
                        comments={comments}
                        rootCommentSort={commentSort}
                        searchActive={
                          searchMode &&
                          !!String(commentSearchText || "").trim() &&
                          !(
                            commentSearchRemote.status === "success" &&
                            commentSearchRemote.matches.length > 0
                          )
                        }
                        searchQuery={commentSearchText}
                        viewerId={viewerId}
                        canParticipate={canParticipate}
                        activeReplyTargetId={activeReplyTargetId}
                        setActiveReplyTargetId={setActiveReplyTargetId}
                        inlineReplyText={inlineReplyText}
                        setInlineReplyText={setInlineReplyText}
                        inlineSending={inlineSending}
                        onSubmitInlineReply={submitInlineReply}
                        highlightedCommentId={highlightedCommentId}
                        focusCommentId={pendingFocusCommentId}
                        slug={effectiveSlug || slug}
                        postId={post?.id ?? postIdParam}
                        onToggleCommentLike={onToggleCommentLike}
                        canModerateComments={canModerateComments}
                        onCommentMutated={onCommentMutated}
                        onCommentActionError={(m) => setActionErr(m || "")}
                        onContinueThread={(commentId, opts) => {
                          if (!post?.id || !effectiveSlug || !commentId) return;
                          const rawFocus = opts?.focusCommentId;
                          const focusNum = Number(rawFocus);
                          const state = Number.isFinite(focusNum) ? { focusCommentId: focusNum } : undefined;
                          navigate(
                            `/clubs/${encodeURIComponent(String(effectiveSlug))}/posts/${encodeURIComponent(
                              String(post.id)
                            )}/comments/${encodeURIComponent(String(commentId))}/thread`,
                            state ? { state } : undefined
                          );
                        }}
                      />
                      {comments.length > 0 && commentsHasMore ? (
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={loadMoreComments}
                            disabled={loadingMoreComments}
                            className={cn(
                              clubThreadShowMoreButtonClass,
                              "gap-1.5",
                              loadingMoreComments ? "opacity-60 cursor-wait pointer-events-none" : ""
                            )}
                          >
                            {loadingMoreComments ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                            ) : null}
                            {loadingMoreComments ? "Loading comments…" : "Load more comments"}
                          </button>
                        </div>
                      ) : null}
                      {comments.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.015] px-5 py-10 sm:py-12 text-center">
                          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                            <MessageCircle className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                          </div>
                          <p className="text-sm font-semibold text-gray-300">No replies yet</p>
                          <p className="mt-1.5 text-xs sm:text-sm text-gray-500 max-w-xs mx-auto leading-relaxed">
                            {canParticipate ? "Start with the box above." : emptyCommentsSecondaryHint}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div className="min-w-0 w-full max-w-full lg:sticky lg:top-20 xl:top-24 lg:self-start">
              {showCommunityChrome ? (
                <aside
                  className="rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-md p-4 sm:p-5 shadow-[0_16px_48px_rgba(0,0,0,0.35)]"
                  aria-label="Community context"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                      <Globe className="h-5 w-5 text-emerald-200/90" strokeWidth={1.75} aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-sm font-semibold text-white">Community</h2>
                      <p className="mt-1 text-xs text-gray-400 leading-relaxed">
                        Public discussion on AnimeHouse. Same thread experience as club posts.
                      </p>
                      <Link
                        to="/community"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-200/95 hover:text-emerald-100 transition-colors"
                      >
                        Community feed
                        <ChevronRight className="h-3.5 w-3.5 opacity-80" aria-hidden />
                      </Link>
                    </div>
                  </div>
                </aside>
              ) : (
                <ClubThreadSidebar
                  club={club}
                  slug={effectiveSlug || slug || ""}
                  clubPublicPath={clubPublicPath}
                  clubHomePath={clubHomePath}
                  accent={accent}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <ClubImageLightbox
        isOpen={previewOpen}
        images={previewImages}
        currentIndex={previewIndex}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewIndex(0);
        }}
        onIndexChange={setPreviewIndex}
      />
    </div>
  );
}
